import { useRuntimeConfig } from '#imports'

import type { DoneInvokeEvent } from 'xstate'

import {
  type ActionType,
  Actions,
  type ActionsResponse,
  type AttributesType,
  type MessageAttributes,
  type OrderlineRequest,
  type ProblemAttributes,
  type RefundAttributes,
} from '~/api/seller-after-sales/types'

import type { MachineContext } from '../ActionBar.machine.type'

const buildAllowedActionsMap = (actions: ActionsResponse['actions']) => {
  return actions.reduce(
    (acc, el) =>
      acc.set(el.action, {
        isDisabled: el.isDisabled,
        attributes: el.attributes,
      }),
    new Map<ActionType, { isDisabled: boolean; attributes: AttributesType }>(),
  )
}

const getActionAttributes = <T>(
  allowedActions: Map<
    ActionType,
    { isDisabled: boolean; attributes: AttributesType }
  >,
  action: ActionType,
): T | undefined => {
  return <T>allowedActions.get(action)?.attributes
}

export const storeActionsAndRequests = (
  context: MachineContext,
  event: DoneInvokeEvent<[ActionsResponse, OrderlineRequest[]]>,
): MachineContext => {
  const actionsData = event.data[0]
  const requestsData = event.data[1]

  const allowedActions = buildAllowedActionsMap(actionsData.actions)

  const { FF_ENABLE_REPORT_PROBLEM_IN_CARE_2_0 } = useRuntimeConfig().public

  const mainActions = {
    isRefundAllowed: allowedActions.has(Actions.refund),
    isReportProblemAllowed: FF_ENABLE_REPORT_PROBLEM_IN_CARE_2_0
      ? allowedActions.has(Actions.problem)
      : false,
    isShipBackToCustomerAllowed: allowedActions.has(
      Actions.createManualReturnOfProductReturn,
    ),
  }

  const sendMessageAttributes = getActionAttributes<MessageAttributes>(
    allowedActions,
    Actions.postMessage,
  )
  const sendMessageInfo = {
    shouldReportProblem: sendMessageAttributes?.reportProblem ?? false,
  }

  const refundAttributes = getActionAttributes<RefundAttributes>(
    allowedActions,
    Actions.refund,
  )

  const refundInfo = refundAttributes
    ? {
        maxRefundPrice: refundAttributes.refundPrice,
        isPartialRefundAllowed: refundAttributes.isAllowedPartial,
        partialRefundReasons: refundAttributes.partialRefundReasons,
        hasActiveInsurancePolicies: refundAttributes.hasActiveInsurancePolicies,
        orderlineQuantity: refundAttributes.orderlineQuantity,
      }
    : null

  const reportProblemInfo = {
    problemTypes:
      getActionAttributes<ProblemAttributes>(allowedActions, Actions.problem)
        ?.types ?? [],
  }

  const { pendingProblems } = actionsData

  return {
    actions: {
      ...mainActions,
      isSendMessageAllowed:
        allowedActions.has(Actions.postMessage) &&
        !sendMessageAttributes?.replyToAgent,
      isSendMessageDisabled:
        allowedActions.get(Actions.postMessage)?.isDisabled ?? false,
      isReplyToAgentAllowed:
        allowedActions.has(Actions.postMessage) &&
        Boolean(sendMessageAttributes?.replyToAgent),
      isRefundDisabled:
        (mainActions.isRefundAllowed &&
          allowedActions.get(Actions.refund)?.isDisabled) ??
        false,
      isReportProblemDisabled:
        (mainActions.isReportProblemAllowed &&
          allowedActions.get(Actions.problem)?.isDisabled) ??
        false,
      isCustomerBlocked: refundAttributes?.isCustomerBlocked ?? false,
    },
    requests: requestsData,
    refundInfo,
    reportProblemInfo,
    pendingProblems,
    sendMessageInfo,
  }
}
